import React, { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { Link, useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import {
  Avatar,
  FiltersBar,
  Icon,
  InfiniteScrollingObserver,
  PeriodFilter,
  SearchInput,
  SingleSelect,
} from '~/components';
import { useApi } from '~/contexts';
import { colors, weights } from '~/styles';
import { intervalOptions, intervalsByScope } from '~/utils';
import humanize from '../utils/humanize';
import {
  EmptyState,
  EntityDetail,
  EntityIcon,
  EntityImage,
  EntityInfo,
  EntityName,
  EntityRow,
  Mono,
  Table,
  Td,
  Th,
} from '../ai/replay/shared';
import EntitySearchFilter from '../ai/replay/EntitySearchFilter';
import WorkspaceDisplay from '../ai/replay/WorkspaceDisplay';
import ActorCell from './ActorCell';

const BuildingIcon = styled(Icon).attrs({ icon: 'building' })`
  font-size: 1rem;
  color: ${colors.grey25};
`;

const BuildingIconSmall = styled(Icon).attrs({ icon: 'building' })`
  font-size: 0.875rem;
  color: ${colors.grey25};
`;

const SelectedLabel = styled.span`
  margin-left: 0.375rem;
`;

const WorkspaceTag = styled(EntityDetail).attrs({ as: 'span' })`
  font-weight: ${weights.normal};
`;

const ClickableRow = styled.tr`
  cursor: pointer;

  &:hover td {
    background: ${colors.grey5};
  }
`;

const SourceLink = styled(Link)`
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Fira Mono', monospace;
  font-size: 0.8125rem;
  font-weight: ${weights.medium};
  color: ${colors.black};

  &:hover {
    color: ${colors.primary};
  }
`;

// Standard filter bar (slim 2rem controls) pinned to four equal columns so the two
// rows read: Search key (two slots) · Workspace · Member / Application · Status · Period.
// FiltersBar carries `flex: 1` for its usual home (a flex-row Page.Filters); here it
// sits in the admin Page's flex *column*, so reset to content height — otherwise it
// grows to fill the page and the two grid rows stretch apart on a short result set.
const Filters = styled(FiltersBar)`
  grid-template-columns: repeat(4, minmax(0, 1fr));
  flex: none;
  align-content: start;
  margin-bottom: 1.5rem;
`;

const SearchSpan = styled.div`
  grid-column: span 2;
`;

// The trace applications (the key's first segment) for the Application filter —
// internal apps plus the integrations whose own software emits traces.
const APPLICATIONS = [
  'web',
  'mobile',
  'macos',
  'api',
  'admin',
  'job',
  'mcp',
  'oauth',
  'slack',
  'bamboohr',
  'expensify',
  'hubspot',
  'qbo',
  'rippling',
  'salesforce',
  'xero',
];

// 'pending' is the pre-open in-memory state and never reaches the log (the first
// emitted event is 'open'), so it is not an observable status here.
const STATUSES = ['running', 'succeeded', 'failed'];

const STATUS_COLORS = {
  succeeded: colors.success,
  failed: colors.danger,
  running: colors.warning,
  pending: colors.grey40,
};

const StatusBadge = styled.span`
  font-size: 0.6875rem;
  font-weight: ${weights.bold};
  text-transform: uppercase;
  color: ${({ $status }) => STATUS_COLORS[$status] || colors.grey40};
`;

function renderWorkspaceItem(item) {
  return (
    <EntityRow>
      <EntityIcon $size="1.5rem">
        {item.imageUrl ? <EntityImage src={item.imageUrl} alt={item.label} /> : <BuildingIcon />}
      </EntityIcon>
      <EntityInfo>
        <EntityName>{item.label}</EntityName>
        {item.key && <EntityDetail>{item.key}</EntityDetail>}
      </EntityInfo>
    </EntityRow>
  );
}

function renderWorkspaceSelected(item) {
  return (
    <EntityRow>
      <EntityIcon $size="1.125rem">
        {item.imageUrl ? <EntityImage src={item.imageUrl} alt={item.label} /> : <BuildingIconSmall />}
      </EntityIcon>
      <SelectedLabel>{item.label}</SelectedLabel>
    </EntityRow>
  );
}

function renderMemberItem(item) {
  return (
    <EntityRow>
      <EntityIcon $size="1.5rem">
        <Avatar value={{ name: item.label, imageUrl: item.imageUrl }} isCircle hasBackground size={24} />
      </EntityIcon>
      <EntityInfo>
        <EntityName>
          {item.label}
          {item.workspaceName && <WorkspaceTag> · {item.workspaceName}</WorkspaceTag>}
        </EntityName>
        <EntityDetail>{item.email}</EntityDetail>
      </EntityInfo>
    </EntityRow>
  );
}

function renderMemberSelected(item) {
  return (
    <EntityRow>
      <EntityIcon $size="1.125rem">
        <Avatar value={{ name: item.label, imageUrl: item.imageUrl }} isCircle hasBackground size={18} />
      </EntityIcon>
      <SelectedLabel>{item.label}</SelectedLabel>
    </EntityRow>
  );
}

export default function TraceList() {
  const api = useApi();
  const history = useHistory();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);

  const [period, setPeriod] = useState(() => {
    const start = urlParams.get('start') || undefined;
    const end = urlParams.get('end') || undefined;
    return start || end ? { key: intervalOptions.custom.key, start, end } : intervalOptions.all_dates;
  });
  const [statusFilter, setStatusFilter] = useState(urlParams.get('status') || '');
  const [application, setApplication] = useState(urlParams.get('application') || '');
  const [searchText, setSearchText] = useState(urlParams.get('q') || '');
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [traces, setTraces] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const start = period.start || undefined;
  const end = period.end || undefined;

  // Resolve workspace/member from URL params on mount.
  const initialWorkspaceId = useRef(urlParams.get('workspaceId'));
  const initialMemberId = useRef(urlParams.get('memberId'));

  useEffect(() => {
    const wId = initialWorkspaceId.current;
    const mId = initialMemberId.current;
    if (!wId && !mId) return;

    const params = {};
    if (wId) params.workspaceId = wId;
    if (mId) params.memberId = mId;

    api.admin.traceReplay.resolve(params).then(({ data }) => {
      if (data.workspace) {
        setSelectedWorkspace({
          id: data.workspace.id,
          label: data.workspace.name,
          key: data.workspace.key,
          imageUrl: data.workspace.imageUrl,
        });
      }
      if (data.member) {
        setSelectedMember({
          id: data.member.id,
          label: data.member.name,
          email: data.member.email,
          imageUrl: data.member.imageUrl,
        });
      }
    });
  }, [api]);

  // Sync filters to URL, and stash the query so TraceDetail's "Back to traces"
  // link can restore the exact filters the user left.
  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedWorkspace) params.set('workspaceId', selectedWorkspace.id);
    if (selectedMember) params.set('memberId', selectedMember.id);
    if (application) params.set('application', application);
    if (searchText) params.set('q', searchText);
    if (start) params.set('start', start);
    if (end) params.set('end', end);
    if (statusFilter) params.set('status', statusFilter);
    const qs = params.toString();
    const search = qs ? `?${qs}` : '';
    sessionStorage.setItem('admin_traces_search', search);
    history.replace({ search });
  }, [selectedWorkspace, selectedMember, application, searchText, start, end, statusFilter, history]);

  const searchDebounceRef = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState(urlParams.get('q') || '');

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchText);
    }, 300);
    return () => clearTimeout(searchDebounceRef.current);
  }, [searchText]);

  const loadMore = useCallback(async () => {
    const params = { from: traces.length };
    if (start) params.start = start;
    if (end) params.end = end;
    if (statusFilter) params.status = statusFilter;
    if (application) params.application = application;
    if (debouncedSearch) params.q = debouncedSearch;
    if (selectedWorkspace) params.workspaceId = selectedWorkspace.id;
    if (selectedMember) params.memberId = selectedMember.id;

    const { data } = await api.admin.traceReplay.list(params);
    setTraces((prev) => [...prev, ...data.traces]);
    setHasMore(data.hasMore);
  }, [api, start, end, statusFilter, application, debouncedSearch, selectedWorkspace, selectedMember, traces.length]);

  useEffect(() => {
    let cancelled = false;
    setTraces([]);
    setHasMore(false);
    setIsLoading(true);

    const fetch = async () => {
      try {
        const params = {};
        if (start) params.start = start;
        if (end) params.end = end;
        if (statusFilter) params.status = statusFilter;
        if (application) params.application = application;
        if (debouncedSearch) params.q = debouncedSearch;
        if (selectedWorkspace) params.workspaceId = selectedWorkspace.id;
        if (selectedMember) params.memberId = selectedMember.id;

        const { data } = await api.admin.traceReplay.list(params);
        if (!cancelled) {
          setTraces(data.traces);
          setHasMore(data.hasMore);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetch();
    return () => {
      cancelled = true;
    };
  }, [api, start, end, statusFilter, application, debouncedSearch, selectedWorkspace, selectedMember]);

  const searchWorkspaces = useCallback(
    async (text, from) => {
      const params = {};
      if (text) params.q = text;
      if (from) params.from = from;
      const { data } = await api.admin.traceReplay.searchWorkspaces(params);
      return {
        items: data.results.map((w) => ({ id: w.id, label: w.name, key: w.key, imageUrl: w.imageUrl })),
        hasMore: data.hasMore,
      };
    },
    [api],
  );

  const openWorkspaces = useCallback(
    async (text, from) => {
      const params = {};
      if (from) params.from = from;
      const { data } = await api.admin.traceReplay.recentWorkspaces(params);
      return {
        items: data.results.map((w) => ({ id: w.id, label: w.name, key: w.key, imageUrl: w.imageUrl })),
        hasMore: data.hasMore,
      };
    },
    [api],
  );

  const mapMembers = useCallback(
    (members) =>
      members.map((m) => ({
        id: m.id,
        label: m.name,
        email: m.email,
        imageUrl: m.imageUrl,
        workspaceId: m.workspace?.id,
        workspaceName: m.workspace?.name,
        workspaceKey: m.workspace?.key,
        workspaceImageUrl: m.workspace?.imageUrl,
      })),
    [],
  );

  const searchMembers = useCallback(
    async (text, from) => {
      const params = {};
      if (text) params.q = text;
      if (from) params.from = from;
      if (selectedWorkspace) params.workspaceId = selectedWorkspace.id;
      const { data } = await api.admin.traceReplay.searchMembers(params);
      return { items: mapMembers(data.results), hasMore: data.hasMore };
    },
    [api, selectedWorkspace, mapMembers],
  );

  const openMembers = useCallback(
    async (text, from) => {
      const params = {};
      if (from) params.from = from;
      if (selectedWorkspace) params.workspaceId = selectedWorkspace.id;
      const { data } = await api.admin.traceReplay.recentMembers(params);
      return { items: mapMembers(data.results), hasMore: data.hasMore };
    },
    [api, selectedWorkspace, mapMembers],
  );

  return (
    <>
      <Filters>
        <SearchSpan>
          <SearchInput
            value={searchText}
            placeholder="Search key"
            onChange={({ target }) => setSearchText(target.value)}
          />
        </SearchSpan>
        <EntitySearchFilter
          placeholder="Workspace"
          selected={selectedWorkspace}
          onSelect={(w) => {
            setSelectedWorkspace(w);
            setSelectedMember(null);
          }}
          onClear={() => setSelectedWorkspace(null)}
          searchFn={searchWorkspaces}
          onOpen={openWorkspaces}
          renderItem={renderWorkspaceItem}
          renderSelected={renderWorkspaceSelected}
        />
        <EntitySearchFilter
          placeholder="Member"
          selected={selectedMember}
          onSelect={(m) => {
            setSelectedMember(m);
            if (!selectedWorkspace && m.workspaceId) {
              setSelectedWorkspace({
                id: m.workspaceId,
                label: m.workspaceName,
                key: m.workspaceKey,
                imageUrl: m.workspaceImageUrl,
              });
            }
          }}
          onClear={() => setSelectedMember(null)}
          searchFn={searchMembers}
          onOpen={openMembers}
          renderItem={renderMemberItem}
          renderSelected={renderMemberSelected}
        />
        <SingleSelect
          placeholder="Application"
          value={application}
          showEmptyOption
          onChange={(e) => setApplication(e.target.value)}>
          {APPLICATIONS.map((a) => (
            <option key={a} value={a}>
              {humanize(a)}
            </option>
          ))}
        </SingleSelect>
        <SingleSelect
          placeholder="Status"
          value={statusFilter}
          showEmptyOption
          onChange={(e) => setStatusFilter(e.target.value)}>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {humanize(s)}
            </option>
          ))}
        </SingleSelect>
        <PeriodFilter
          name="period"
          placeholder="Period"
          scope="day"
          intervals={[intervalOptions.all_dates, ...intervalsByScope.day]}
          value={period}
          onChange={({ target: { value } }) => setPeriod(value)}
        />
      </Filters>

      {isLoading && <EmptyState>Loading traces...</EmptyState>}

      {!isLoading && traces.length === 0 && <EmptyState>No traces found.</EmptyState>}

      {!isLoading && traces.length > 0 && (
        <>
          <Table>
            <thead>
              <tr>
                <Th>Trace</Th>
                <Th>Workspace</Th>
                <Th>Actor</Th>
                <Th>Status</Th>
                <Th>Started</Th>
              </tr>
            </thead>
            <tbody>
              {traces.map((c) => (
                <ClickableRow key={c.traceId} onClick={() => history.push(`/admin/traces/${c.traceId}`)}>
                  <Td>
                    <SourceLink to={`/admin/traces/${c.traceId}`} onClick={(e) => e.stopPropagation()}>
                      {c.source || '-'}
                    </SourceLink>
                    <div>
                      <Mono>{c.traceId}</Mono>
                    </div>
                  </Td>
                  <Td>
                    <WorkspaceDisplay workspace={c.workspace} />
                  </Td>
                  <Td>
                    <ActorCell
                      actor={c.actor}
                      member={c.member}
                      apiKeyId={c.apiKeyId}
                      admin={c.admin}
                      application={c.application}
                      credential={c.credential}
                    />
                  </Td>
                  <Td>
                    <StatusBadge $status={c.status}>{c.status}</StatusBadge>
                  </Td>
                  <Td title={c.startedAt}>{moment(c.startedAt).fromNow()}</Td>
                </ClickableRow>
              ))}
            </tbody>
          </Table>

          {hasMore && <InfiniteScrollingObserver key={traces.length} onIntersecting={loadMore} />}
        </>
      )}
    </>
  );
}
